import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Masonry from "react-masonry-css";
import {
  ShieldCheck,
  MapPin,
  Search,
  CreditCard,
  Star,
  ChevronDown,
  ChevronUp,
  Edit3,
} from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useModal } from "../context/ModalContext";
import VerifiedIcon from "@mui/icons-material/Verified";
import CallSellerButton from "../components/CallSellerButton";
import ChatButton from "../components/ChatButton";
import DetailSkeleton from "../components/DetailSkeleton";

const apiUrl = process.env.REACT_APP_API_URL;

export default function DetailPage() {
  const { showAlert } = useModal();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);
  const [comment, setComment] = useState("");
  const [viewerOpen, setViewerOpen] = useState(false);
  const [zoom, setZoom] = useState(1);

  // Accordion states
  const [openSection, setOpenSection] = useState("description");

  useEffect(() => {
    fetch(`${apiUrl}/api/posts/${id}`)
      .then((res) => res.json())
      .then(setData)
      .catch(console.error);
  }, [id]);

  useEffect(() => {
    if (viewerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [viewerOpen]);

  if (!data)
    return <DetailSkeleton />;

  const { post, rating, reviews, similar } = data;

  // Merge meta + meta_ids: use meta_ids value when meta value is null/empty
  const mergedMeta = { ...(post.meta || {}) };
  if (post.meta_ids) {
    Object.keys(post.meta_ids).forEach((k) => {
      if ((mergedMeta[k] === null || mergedMeta[k] === undefined || mergedMeta[k] === "") && post.meta_ids[k]) {
        mergedMeta[k] = post.meta_ids[k];
      }
    });
  }
  // Read condition also from meta data or post.condition
  if (!mergedMeta.Condition && (post.condition || mergedMeta.condition)) {
    mergedMeta.Condition = post.condition || mergedMeta.condition;
  }

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? "" : section);
  };

  const uStr = localStorage.getItem("user") || sessionStorage.getItem("user");
  const user = uStr ? JSON.parse(uStr) : null;
  const isOwner = user && user.id === (post.user_id || post.userId);

  return (
    <div className="bg-white min-h-screen font-sans text-gray-900">
      <Header />


      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Breadcrumb / Back */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link
            to="/"
            className="hover:text-gray-900 transition flex items-center gap-1"
          >
            Home
          </Link>
          <span>/</span>
          <span className="text-gray-900 font-medium truncate max-w-[200px]">
            {mergedMeta.Model || post.ItemName || post.title}
          </span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-20">
          {/* LEFT COLUMN - IMAGES */}
          <div className="space-y-6">
            <div
              className="bg-gray-100 rounded-lg overflow-hidden relative group aspect-[4/5] cursor-zoom-in"
              onClick={() => setViewerOpen(true)}
            >
              <img
                src={post.images[activeImg]}
                alt={post.ItemName}
                className="w-full h-full object-cover object-center transition duration-500 group-hover:scale-105"
              />
              <div className="absolute top-4 right-4 bg-white p-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition">
                <Search className="w-5 h-5 text-gray-700" />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              {post.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`relative aspect-square rounded-md overflow-hidden bg-gray-50 border-2 transition-all ${activeImg === i
                    ? "border-gray-900 ring-1 ring-gray-900"
                    : "border-transparent hover:border-gray-300"
                    }`}
                >
                  <img
                    src={img}
                    alt="thumbnail"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* RIGHT COLUMN - DETAILS */}
          <div className="flex flex-col">
            <div className="mb-2">
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 mb-2">
                {post.ItemName}
              </h1>
              <p className="text-lg text-gray-500 mb-4">
                {mergedMeta.Brand} {mergedMeta.Model}
              </p>
            </div>

            <div className="flex items-baseline gap-4 mb-4">
              <span className="text-3xl font-bold text-gray-900">
                {Number(post.price).toLocaleString("en-US")} ETB
              </span>
            </div>

            {/* Mock Ratings Mini Display */}
            <div className="flex items-center gap-2 mb-4 justify-between">
              <div className="flex gap-2">
                <div className="flex text-yellow-400 text-sm">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className={`w-4 h-4 ${s <= Math.round(rating.avgRating || 0)
                        ? "fill-current"
                        : "text-gray-300"
                        }`}
                    />
                  ))}
                </div>
                <a href="#review">
                  <span className="text-sm text-gray-500 underline decoration-gray-300 underline-offset-4 cursor-pointer">
                    {rating.total} reviews
                  </span>
                </a>
              </div>
              {post.verified === 1 && (
                <div className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg flex items-center gap-1 shadow-sm border border-blue-50">
                  <VerifiedIcon className="text-green-500 !w-4 !h-4" />
                  <span className="text-[10px] font-bold text-green-700 uppercase tracking-tight">Verified</span>
                </div>
              )}
            </div>

            {/* Separator */}
            <div className="h-px bg-gray-200 mb-8" />

            {/* DYNAMIC META / SELECTORS (Mocked Style) */}
            <div className="space-y-6 mb-8">
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                {Object.entries(mergedMeta)
                  .filter(([, v]) => v !== null && v !== undefined && v !== "")
                  .map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <span className="text-gray-500">{k}:</span>
                      <span className="text-gray-900 font-medium">{v}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* ACTION BUTTONS */}

            {/* SELLER PROFILE */}
            <div className="bg-gray-50 rounded-2xl p-6 mb-8 border border-gray-100">
              <div className="flex items-center gap-4">
                <img
                  src={post.sellerPicture || `https://ui-avatars.com/api/?name=${post.sellerName}`}
                  alt={post.sellerName}
                  className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-sm"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-gray-900">{post.sellerName}</h3>
                    {post.verified === 1 && <VerifiedIcon className="text-blue-500 !w-4 !h-4" />}
                  </div>
                  <p className="text-xs text-gray-500">Member since {new Date(post.created_at).getFullYear()}</p>
                </div>
                <div className="text-right">
                  <div className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded-md uppercase">
                    Active Seller
                  </div>
                </div>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            {isOwner ? (
              <div className="bg-blue-50 rounded-2xl p-6 mb-10 border border-blue-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center shadow-lg">
                    <Edit3 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-blue-900">This is your post</h3>
                    <p className="text-xs text-blue-700">You can update details or manage this listing.</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/edit-post/${post.id || post.ID}`)}
                  className="mt-4 w-full py-4 bg-white text-blue-600 border-2 border-blue-200 rounded-xl font-black shadow-sm hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-300"
                >
                  Edit Listing
                </button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <CallSellerButton phone={post.sellerPhone} />
                <ChatButton postId={post.id || post.ID} sellerId={post.user_id || post.userId} />
              </div>
            )}

            {/* ACCORDIONS */}
            <div className="border-t border-gray-200">
              {/* Description */}
              <div className="border-b border-gray-200">
                <button
                  onClick={() => toggleSection("description")}
                  className="w-full flex items-center justify-between py-5 text-left group"
                >
                  <span className="font-medium text-gray-900 group-hover:text-gray-600 transition">
                    Product Description
                  </span>
                  {openSection === "description" ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${openSection === "description"
                    ? "max-h-96 pb-5 opacity-100"
                    : "max-h-0 opacity-0"
                    }`}
                >
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                    {post.description}
                  </p>
                </div>
              </div>

              {/* Safety Tips */}
              <div className="border-b border-gray-200">
                <button
                  onClick={() => toggleSection("safety")}
                  className="w-full flex items-center justify-between py-5 text-left group"
                >
                  <span className="font-medium text-gray-900 group-hover:text-gray-600 transition">
                    Safety Tips
                  </span>
                  {openSection === "safety" ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${openSection === "safety"
                    ? "max-h-96 pb-5 opacity-100"
                    : "max-h-0 opacity-0"
                    }`}
                >
                  <ul className="space-y-3 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" /> Meet in safe
                      public places
                    </li>
                    <li className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-gray-400" /> Inspect item
                      before paying
                    </li>
                    <li className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-gray-400" /> Don't pay
                      in advance
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* REVIEWS SECTION */}
        <section className="mt-24 border-t border-gray-200 pt-16" id="review">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Rating Summary */}
            <div className="md:col-span-1 space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Driver Reviews
              </h2>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold">
                  {Number(rating.avgRating || 0).toFixed(1)}
                </span>
                <span className="text-gray-500">/ 5.0</span>
              </div>
              <div className="flex gap-1 text-yellow-400 text-lg">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    className={`w-5 h-5 ${s <= Math.round(rating.avgRating || 0)
                      ? "fill-current"
                      : "text-gray-200"
                      }`}
                  />
                ))}
              </div>
              <p className="text-sm text-gray-500">
                Based on {rating.total} reviews
              </p>

              {/* Write Review Form Toggle? Or inline */}
              <div className="bg-gray-50 p-6 rounded-xl mt-6">
                <h3 className="font-semibold mb-4">Write a Review</h3>
                <div className="space-y-3">
                  <div className="flex gap-2 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setSelectedRating(star)}
                        className="focus:outline-none"
                      >
                        <Star
                          className={`w-6 h-6 ${star <= selectedRating
                            ? "text-yellow-400 fill-current"
                            : "text-gray-300"
                            }`}
                        />
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Share your thoughts..."
                    className="w-full text-sm p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
                    rows={3}
                  />
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
                      if (!token || token === "null" || token === "undefined") {
                        navigate("/login");
                        return;
                      }

                      // Logic extracted from original
                      try {
                        const res = await fetch(
                          `${apiUrl}/api/review`,
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
                            },
                            body: JSON.stringify({
                              postId: id,
                              rating: selectedRating,
                              comment,
                            }),
                          },
                        );
                        const body = await res.json();
                        if (!res.ok) throw new Error(body.message);
                        showAlert("Review submitted!", "success");
                        setSelectedRating(0);
                        setComment("");
                      } catch (err) {
                        showAlert(err.message || "Something went wrong.", "error");
                      }
                    }}
                    className="w-full bg-gray-900 text-white text-sm font-semibold py-2.5 rounded-lg hover:bg-gray-800 transition"
                  >
                    Submit Review
                  </button>
                </div>
              </div>
            </div>

            {/* Review List */}
            <div className="md:col-span-2 space-y-6 bg-gray-50 p-4 rounded-xl">
              {reviews.length === 0 ? (
                <p className="text-gray-500 italic">
                  No reviews yet. Be the first!
                </p>
              ) : (
                reviews.map((r, i) => (
                  <div key={i} className="bg-gray-50 p-6 rounded-xl">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={r.picture || "/avatar.png"}
                          alt={r.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {r.name}
                          </h4>
                          <div className="flex text-yellow-400 text-xs">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star
                                key={s}
                                className={`w-3 h-3 ${s <= r.rating
                                  ? "fill-current"
                                  : "text-gray-300"
                                  }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(r.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {r.comment}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        {/* SIMILAR ITEMS */}
        <section className="mt-24 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            You might also like
          </h2>

          <Masonry
            breakpointCols={{ default: 4, 1024: 3, 768: 2 }}
            className="my-masonry-grid"
            columnClassName="my-masonry-grid_column"
          >
            {similar.map((p) => (
              <Link
                key={p.id}
                to={`/detail/${p.id}`}
                className="break-inside-avoid block mb-6"
              >
                <div className="bg-white rounded-2xl shadow hover:shadow-xl transition overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full object-contain"
                  />

                  <div className="p-3 space-y-1">
                    <div className="flex items-center gap-1">
                      <p className="font-medium text-gray-800 leading-tight">
                        {p.title}
                      </p>

                      {p.verified === 1 && (
                        <VerifiedIcon className="text-blue-500 !w-4 !h-4" />
                      )}
                    </div>

                    {(p.brand || p.model) && (
                      <p className="text-sm text-gray-500">
                        {p.brand} {p.model} {p.storage && `• ${p.storage}`}
                      </p>
                    )}

                    <div className="flex justify-between items-center">
                      <p className="text-green-600 font-semibold">
                        {p.price} ETB
                      </p>

                      {(p.condition || p.meta?.Condition) && (
                        <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">
                          {p.condition || p.meta?.Condition}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </Masonry>
        </section>
      </main>

      {/* FULLSCREEN VIEWER */}
      {viewerOpen && (
        <div
          className="fixed inset-0 z-[9999] bg-white/95 backdrop-blur-sm flex items-center justify-center"
          onClick={() => {
            setViewerOpen(false);
            setZoom(1);
          }}
        >
          <button
            onClick={() => {
              setViewerOpen(false);
              setZoom(1);
            }}
            className="absolute top-6 right-6 p-4 hover:bg-gray-100 rounded-full transition"
          >
            <span className="text-2xl">✕</span>
          </button>

          <img
            src={post.images[activeImg]}
            alt="Full view"
            className="max-h-[90vh] max-w-[90vw] object-contain shadow-2xl"
            style={{
              transform: `scale(${zoom})`,
              transition: "transform 0.2s",
            }}
            onClick={(e) => e.stopPropagation()}
            onWheel={(e) => {
              e.stopPropagation();
              setZoom((z) =>
                Math.max(1, Math.min(3, z + (e.deltaY < 0 ? 0.2 : -0.2))),
              );
            }}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}
